import type { InputHTMLAttributes, ReactNode } from "react";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "children"> {
  /** Optional label rendered to the side of the box. */
  label?: ReactNode;
  /** Optional description rendered under the label. */
  description?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const sizeClasses: Record<NonNullable<CheckboxProps["size"]>, string> = {
  sm: "h-4 w-4",
  md: "h-[18px] w-[18px]",
  lg: "h-[22px] w-[22px]",
};

const checkIconSize: Record<NonNullable<CheckboxProps["size"]>, string> = {
  sm: "h-3 w-3",
  md: "h-3.5 w-3.5",
  lg: "h-[18px] w-[18px]",
};

/**
 * Global checkbox primitive. Accent-filled when checked, consistent across
 * seller / admin / buyer apps. Use `label` + `description` for stacked
 * content, or render the raw input (omit label) for a bare checkbox inside
 * a custom layout.
 */
export function Checkbox({
  label,
  description,
  size = "md",
  className = "",
  disabled,
  ...rest
}: CheckboxProps) {
  const input = (
    <span className="relative inline-flex shrink-0 items-center">
      <input
        type="checkbox"
        disabled={disabled}
        className={[
          "peer",
          sizeClasses[size],
          "cursor-pointer appearance-none rounded-[4px] border border-[color:var(--border-strong)] bg-[color:var(--surface)]",
          "transition-colors",
          "checked:border-[color:var(--accent-500)] checked:bg-[color:var(--accent-500)]",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
          "disabled:cursor-not-allowed disabled:opacity-60",
        ].join(" ")}
        {...rest}
      />
      <svg
        viewBox="0 0 20 20"
        aria-hidden="true"
        className={[
          "pointer-events-none absolute inset-0 m-auto text-white opacity-0 transition-opacity peer-checked:opacity-100",
          checkIconSize[size],
        ].join(" ")}
      >
        <path
          d="M5 10.5l3 3 7-7"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );

  if (!label && !description) {
    return <span className={className}>{input}</span>;
  }

  return (
    <label
      className={[
        "inline-flex items-start gap-2",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className,
      ].join(" ")}
    >
      {input}
      <span className="flex min-w-0 flex-col gap-0.5">
        {label ? (
          <span className="text-card leading-snug text-[color:var(--text-primary)]">
            {label}
          </span>
        ) : null}
        {description ? (
          <span className="text-meta leading-snug text-[color:var(--text-muted)]">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  );
}
